import * as React from 'react';
import { Location } from 'lib/types';
import usePrevious from 'core/hooks/usePrevious';
import isEqual from 'lodash/isEqual';
import { Slot } from 'booking/lib/types';
import NearbySlotsContext, { NearbySlots } from './context';
import SlotsLoader from './SlotsLoader';

interface NearbySlotsProviderProps {
    nearbyLocations: ReadonlyArray<Location> | null;
    day: string;
    loadSlots: boolean;
}

function createInitialState(
    nearbyLocations: ReadonlyArray<Location> | null,
): Record<string, NearbySlots> {
    return (
        nearbyLocations?.reduce<Record<string, NearbySlots>>(
            (aggregator, location) => ({
                ...aggregator,
                [location._id]: {
                    isLoading: true,
                    slots: [],
                },
            }),
            {},
        ) ?? {}
    );
}

export default function NearbySlotsProvider({
    nearbyLocations,
    day,
    children,
    loadSlots,
}: React.PropsWithChildren<NearbySlotsProviderProps>) {
    const previousLocations = usePrevious(nearbyLocations);

    const [locations, setLocations] = React.useState<
        Record<string, NearbySlots>
    >({});

    React.useEffect(() => {
        if (
            nearbyLocations !== previousLocations &&
            !isEqual(nearbyLocations, previousLocations)
        ) {
            setLocations(createInitialState(nearbyLocations));
        }
    }, [nearbyLocations, previousLocations, loadSlots, day]);

    const hasSlots = React.useMemo(
        () =>
            Object.values(locations).some(
                ({ slots, isLoading }) => isLoading || slots.length > 0,
            ),
        [locations],
    );

    const setSlots = React.useCallback(
        (
            locationId: string,
            isLoading: boolean,
            slots: ReadonlyArray<Slot>,
            error?: unknown,
        ) => {
            setLocations((previous) => ({
                ...previous,
                [locationId]: {
                    isLoading,
                    slots,
                    error,
                },
            }));
        },
        [],
    );

    return (
        <NearbySlotsContext.Provider value={{ hasSlots, locations }}>
            {children}
            {loadSlots &&
                day !== '' &&
                nearbyLocations?.map((location) => (
                    <SlotsLoader
                        key={location._id}
                        day={day}
                        locationId={location._id}
                        setSlots={setSlots}
                    />
                ))}
        </NearbySlotsContext.Provider>
    );
}
